import * as React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { changePassword } from '@/services/user';
import { useTranslation } from 'react-i18next';
import i18next from '@/i18next';
const createChangePasswordSchema = (hasPassword) => z
    .object({
    currentPassword: hasPassword
        ? z.string().min(1, i18next.t('security.password_dialog.current_required', { ns: 'profile' }))
        : z.string().optional(),
    plainPassword: z.object({
        first: z.string().min(8, i18next.t('security.password_dialog.min_length', { ns: 'profile' })),
        second: z.string(),
    }),
})
    .refine((data) => data.plainPassword.first === data.plainPassword.second, {
    path: ['plainPassword', 'second'],
    message: i18next.t('security.password_dialog.mismatch', { ns: 'profile' }),
});
export const PasswordDialog = ({ hasPassword }) => {
    const { t } = useTranslation('profile');
    const schema = React.useMemo(() => createChangePasswordSchema(hasPassword), [hasPassword]);
    const form = useForm({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: {
            currentPassword: '',
            plainPassword: {
                first: '',
                second: '',
            },
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: changePassword,
    });
    const onSubmit = React.useCallback((values) => {
        mutate(values);
    }, [mutate]);
    return (<Dialog>
            <DialogTrigger asChild>
                <Button variant={hasPassword ? 'outline' : 'default'} size="sm">
                    {hasPassword ? t('security.change') : t('security.set_password')}
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {hasPassword
            ? t('security.password_dialog.title_change')
            : t('security.password_dialog.title_set')}
                    </DialogTitle>
                    <DialogDescription>
                        {hasPassword
            ? t('security.password_dialog.desc_change')
            : t('security.password_dialog.desc_set')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <div className="space-y-3">
                        {hasPassword && (<TextField control={form.control} name="currentPassword" label={t('security.password_dialog.current')} type="password" autoComplete="current-password" disabled={isPending}/>)}

                        <TextField control={form.control} name="plainPassword.first" label={t('security.password_dialog.new')} type="password" autoComplete="new-password" disabled={isPending}/>

                        <TextField control={form.control} name="plainPassword.second" label={t('security.password_dialog.confirm')} type="password" autoComplete="new-password" disabled={isPending}/>
                    </div>
                </Form>

                <DialogFooter>
                    <Button type="submit" disabled={!form.formState.isValid || isPending} onClick={form.handleSubmit(onSubmit)}>
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                        {hasPassword
            ? t('security.password_dialog.submit_change')
            : t('security.password_dialog.submit_set')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
};
PasswordDialog.displayName = 'PasswordDialog';
